import React, { Context, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserState } from './user-state.provider.ts';
import { isAdmin } from '../utils/roles';
import { subscriptionGrantsAccess } from '../utils/subscription';
import type { Subscription } from '../types/payment.types';
import { refreshUserInfo } from '../services/user.service';
import { usePageActivationRefresh } from '../hooks/usePageActivationRefresh';

interface SubscriptionStatusContextType {
    hasActiveSubscription: boolean;
    isSubscriptionLoading: boolean;
    subscription: Subscription | null;
    refreshSubscription: () => Promise<Subscription | null>;
}

interface SubscriptionStatusProviderProps {
    children: JSX.Element | JSX.Element[];
}

const SubscriptionStatusContext: Context<SubscriptionStatusContextType> = createContext<SubscriptionStatusContextType>(null!);

const SubscriptionStatusProvider = ({ children }: SubscriptionStatusProviderProps) => {
    const { isLoggedIn, isUserStateLoading, getUserInfo, setUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const authKey = isLoggedIn ? userInfo?.token ?? '' : '';
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [subscriptionAuthKey, setSubscriptionAuthKey] = useState(authKey);
    const isMountedRef = useRef(true);
    const refreshPromiseRef = useRef<{ authKey: string; promise: Promise<Subscription | null> } | null>(null);
    const trailingRefreshPromiseRef = useRef<{ authKey: string; promise: Promise<Subscription | null> } | null>(null);
    const subscriptionRef = useRef<Subscription | null>(null);
    const authKeyRef = useRef(authKey);
    const subscriptionAuthKeyRef = useRef(authKey);
    // Invalidate old-account requests before effects run so stale state is never exposed for one render.
    if (authKeyRef.current !== authKey) {
        authKeyRef.current = authKey;
        refreshPromiseRef.current = null;
        trailingRefreshPromiseRef.current = null;
    }

    const applySubscriptionState = useCallback((ownerAuthKey: string, nextSubscription: Subscription | null, grantsAccess: boolean) => {
        if (!isMountedRef.current || authKeyRef.current !== ownerAuthKey) {
            return;
        }

        subscriptionAuthKeyRef.current = ownerAuthKey;
        subscriptionRef.current = nextSubscription;
        setSubscriptionAuthKey(ownerAuthKey);
        setSubscription(nextSubscription);
        setHasActiveSubscription(grantsAccess);
    }, []);

    const fetchSubscriptionStatus = useCallback(async (requestedAuthKey: string): Promise<Subscription | null> => {
        const requestUser = getUserInfo();
        if (!isLoggedIn || !requestedAuthKey || requestUser?.token !== requestedAuthKey) {
            if (isMountedRef.current) {
                subscriptionAuthKeyRef.current = requestedAuthKey;
                subscriptionRef.current = null;
                setSubscriptionAuthKey(requestedAuthKey);
                setSubscription(null);
                setHasActiveSubscription(false);
                setIsSubscriptionLoading(false);
            }
            return null;
        }

        if (isAdmin(requestUser.roles)) {
            applySubscriptionState(requestedAuthKey, null, true);
            if (isMountedRef.current && authKeyRef.current === requestedAuthKey) {
                setIsSubscriptionLoading(false);
            }
            return null;
        }

        if (isMountedRef.current) {
            setIsSubscriptionLoading(true);
        }

        try {
            const { getSubscriptionDetails } = await import('../services/payment.service');
            const nextSubscription = await getSubscriptionDetails(requestedAuthKey);
            if (authKeyRef.current !== requestedAuthKey) {
                return null;
            }

            const grantsAccess = subscriptionGrantsAccess(nextSubscription);
            applySubscriptionState(requestedAuthKey, nextSubscription, grantsAccess);

            const hasSubscriberRole = (requestUser.roles ?? [])
                .some((role) => role.toLowerCase() === 'subscriber');
            if (hasSubscriberRole !== grantsAccess) {
                try {
                    const refreshedUser = await refreshUserInfo(requestedAuthKey);
                    if (authKeyRef.current !== requestedAuthKey) {
                        return null;
                    }

                    const currentRoles = [...(requestUser.roles ?? [])].sort().join('|');
                    const refreshedRoles = [...(refreshedUser.roles ?? [])].sort().join('|');
                    if (isMountedRef.current && currentRoles !== refreshedRoles) {
                        setUserInfo({ ...requestUser, ...refreshedUser, token: refreshedUser.token });
                    }
                } catch {
                    console.error('Failed to refresh user roles after subscription change');
                }
            }
            return nextSubscription;
        } catch (error: any) {
            if (authKeyRef.current !== requestedAuthKey) {
                return null;
            }

            if (error?.response?.status === 404) {
                applySubscriptionState(requestedAuthKey, null, false);

                const hasSubscriberRole = (requestUser.roles ?? [])
                    .some((role) => role.toLowerCase() === 'subscriber');
                if (hasSubscriberRole) {
                    try {
                        const refreshedUser = await refreshUserInfo(requestedAuthKey);
                        if (authKeyRef.current !== requestedAuthKey) {
                            return null;
                        }

                        const currentRoles = [...(requestUser.roles ?? [])].sort().join('|');
                        const refreshedRoles = [...(refreshedUser.roles ?? [])].sort().join('|');
                        if (isMountedRef.current && currentRoles !== refreshedRoles) {
                            setUserInfo({ ...requestUser, ...refreshedUser, token: refreshedUser.token });
                        }
                    } catch {
                        console.error('Failed to refresh user roles after subscription change');
                    }
                }
                return null;
            } else {
                if (subscriptionAuthKeyRef.current !== requestedAuthKey) {
                    applySubscriptionState(requestedAuthKey, null, false);
                }
                console.error('Failed to fetch subscription status');
            }
            return subscriptionAuthKeyRef.current === requestedAuthKey
                ? subscriptionRef.current
                : null;
        } finally {
            if (isMountedRef.current && authKeyRef.current === requestedAuthKey) {
                setIsSubscriptionLoading(false);
            }
        }
    }, [applySubscriptionState, getUserInfo, isLoggedIn, setUserInfo]);

    const startSubscriptionRefresh = useCallback((requestedAuthKey: string): Promise<Subscription | null> => {
        const refreshPromise = fetchSubscriptionStatus(requestedAuthKey);
        refreshPromiseRef.current = { authKey: requestedAuthKey, promise: refreshPromise };
        void refreshPromise.finally(() => {
            if (refreshPromiseRef.current?.promise === refreshPromise) {
                refreshPromiseRef.current = null;
            }
        });
        return refreshPromise;
    }, [fetchSubscriptionStatus]);

    const refreshSubscription = useCallback((): Promise<Subscription | null> => {
        const inFlightRefresh = refreshPromiseRef.current;
        if (inFlightRefresh?.authKey !== authKey) {
            return startSubscriptionRefresh(authKey);
        }

        if (trailingRefreshPromiseRef.current?.authKey === authKey) {
            return trailingRefreshPromiseRef.current.promise;
        }

        const requestedAuthKey = authKey;
        const trailingPromise = inFlightRefresh.promise
            .catch(() => null)
            .then(() => {
                if (trailingRefreshPromiseRef.current?.promise === trailingPromise) {
                    trailingRefreshPromiseRef.current = null;
                }
                return authKeyRef.current === requestedAuthKey
                    ? startSubscriptionRefresh(requestedAuthKey)
                    : null;
            });
        trailingRefreshPromiseRef.current = { authKey: requestedAuthKey, promise: trailingPromise };
        void trailingPromise.finally(() => {
            if (trailingRefreshPromiseRef.current?.promise === trailingPromise) {
                trailingRefreshPromiseRef.current = null;
            }
        });
        return trailingPromise;
    }, [authKey, startSubscriptionRefresh]);

    useEffect(() => {
        isMountedRef.current = true;

        const loadSubscription = async () => {
            if (isUserStateLoading) {
                return;
            }

            await refreshSubscription();
        };

        loadSubscription();

        return () => {
            isMountedRef.current = false;
        };
    }, [isUserStateLoading, refreshSubscription]);

    usePageActivationRefresh(
        isLoggedIn && !isUserStateLoading,
        refreshSubscription
    );

    const currentUserIsAdmin = isAdmin(userInfo?.roles);
    const hasCurrentAuthState = subscriptionAuthKey === authKey;
    const value = useMemo(() => ({
        hasActiveSubscription: currentUserIsAdmin || (hasCurrentAuthState ? hasActiveSubscription : false),
        isSubscriptionLoading: !currentUserIsAdmin && !!authKey && (!hasCurrentAuthState || isSubscriptionLoading),
        subscription: hasCurrentAuthState ? subscription : null,
        refreshSubscription
    }), [authKey, currentUserIsAdmin, hasActiveSubscription, hasCurrentAuthState, isSubscriptionLoading, refreshSubscription, subscription]);

    return (
        <SubscriptionStatusContext.Provider value={value}>
            {children}
        </SubscriptionStatusContext.Provider>
    );
};

export const useSubscriptionStatus = () => {
    return useContext(SubscriptionStatusContext);
};

export default SubscriptionStatusProvider;
